/**
 * /sso/consume — terminal page in the inbound-SSO hand-off (Feb 2026).
 *
 * The user lands here from /api/sso/in's 302 redirect with a one-time
 * token `ot` in the URL. We POST it to /api/sso/consume which marks the
 * OT consumed and returns a fresh Bearer token + the partner-supplied
 * redirect path. We install the token via setAuthToken, refresh the
 * AuthContext, and navigate to the redirect (allowlist-validated server-
 * side so an attacker can't pivot to an external host).
 *
 * Security note: the page is mounted at /sso/consume (no /api prefix),
 * lives behind no auth gate (we're literally about to authenticate the
 * user), and never echoes the OT back into the URL after consumption.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function SsoConsume() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    const ot = params.get("ot");
    if (!ot) { setError("Missing SSO token. Please return to the partner site and try again."); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/sso/consume", { ot });
        if (cancelled) return;
        // Persist the new bearer token, then refresh the AuthContext so
        // ProtectedRoute below sees the authenticated user before we
        // navigate to the gated redirect path.
        setAuthToken(data.token);
        await refresh?.();
        nav(data.redirect || "/onboarding", { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "We couldn't sign you in. The link may have expired — please return to the partner site and try again.");
      }
    })();
    return () => { cancelled = true; };
  }, [params, nav, refresh]);

  return (
    <div
      data-testid="sso-consume-page"
      className="min-h-screen bg-[#04302E] text-white flex items-center justify-center px-4"
    >
      <div className="max-w-md text-center">
        {!error ? (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#7BD3CE]" data-testid="sso-consume-spinner"/>
            <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-[#7BD3CE]">
              Signing you in
            </p>
            <p className="mt-2 text-xs text-[#7BD3CE99]">
              One moment while we hand you off securely.
            </p>
          </>
        ) : (
          <>
            <AlertCircle className="mx-auto h-8 w-8 text-amber-300" data-testid="sso-consume-error-icon"/>
            <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-amber-300">
              Couldn't sign you in
            </p>
            <p data-testid="sso-consume-error-text" className="mt-2 text-sm text-white/80">
              {error}
            </p>
            <Link
              data-testid="sso-consume-login-link"
              to="/login"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#7BD3CE] px-4 py-2 text-sm font-bold text-[#04302E] hover:bg-white"
            >Go to login</Link>
          </>
        )}
      </div>
    </div>
  );
}
